import { Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageBubble } from "@/features/threads/components/MessageBubble";

import type { HelpMessage } from "../hooks/useHelpChat";

interface Props {
  messages: HelpMessage[];
  streaming: boolean;
  onSend: (text: string) => void;
}

export function HelpChat({ messages, streaming, onSend }: Props) {
  const { t } = useTranslation("help");
  const [text, setText] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  function doSend() {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;
    onSend(trimmed);
    setText("");
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      doSend();
    }
  }

  return (
    <div className="flex h-full flex-col gap-3">
      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto pr-1">
        {messages.length === 0 ? (
          <p className="px-2 py-8 text-center text-sm text-muted-foreground">
            {t("greeting")}
          </p>
        ) : (
          messages.map((m, i) => (
            <MessageBubble
              key={i}
              role={m.role}
              content={m.content}
              streaming={
                streaming &&
                i === messages.length - 1 &&
                m.role === "assistant"
              }
            />
          ))
        )}
      </div>
      <div className="border-t pt-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t("placeholder")}
          rows={2}
          disabled={streaming}
        />
        <div className="mt-2 flex justify-end">
          <Button onClick={doSend} disabled={streaming || text.trim() === ""}>
            <Send className="h-4 w-4" />
            {t("send")}
          </Button>
        </div>
      </div>
    </div>
  );
}
